// Общие типы, используемые сервером и клиентом.

/** Ресурсы. Серебро — основная валюта; золото — донатная валюта (в коде и БД — gems). */
export const RESOURCE_TYPES = ['silver', 'wood', 'stone', 'food', 'iron'] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];
export type ResourceMap = Record<ResourceType, number>;
export type PartialResources = Partial<Record<ResourceType, number>>;

export const RACES = ['humans', 'elves', 'dwarves', 'orcs'] as const;
export type RaceKey = (typeof RACES)[number];

export const HERO_CLASSES = ['warrior', 'mage', 'ranger', 'corsair'] as const;
export type HeroClass = (typeof HERO_CLASSES)[number];

export const HERO_STATS = ['strength', 'agility', 'intellect', 'endurance', 'luck'] as const;
/**
 * Навыки героя: очки уровня вкладываются прямо в понятный эффект. Боевой герой качает атаку, защиту,
 * живучесть и скорость; добытчик — добычу в шахтах и грабёж; разведчик — разведку и скорость; капитан — флот.
 */
export const HERO_TALENTS = [
  'attack',
  'defense',
  'vitality',
  'speed',
  'plunder',
  'mining',
  'scouting',
  'naval',
  'storm',
] as const;
export type HeroTalent = (typeof HERO_TALENTS)[number];
export type HeroStat = (typeof HERO_STATS)[number];

export const EQUIPMENT_SLOTS = [
  'helmet',
  'armor',
  'weapon',
  'shield',
  'boots',
  'cloak',
  'ring',
  'amulet',
  'artifact',
] as const;
export type EquipmentSlotType = (typeof EQUIPMENT_SLOTS)[number];

export const RARITIES = ['common', 'rare', 'epic', 'legendary'] as const;
export type Rarity = (typeof RARITIES)[number];

export const BUILDING_TYPES = [
  'town_hall',
  'barracks',
  'warehouse',
  'tavern',
  'forge',
  'hero_temple',
  'port',
  'shipyard',
  'wall',
  'farm',
  'sawmill',
  'quarry',
  'iron_mine',
  'silver_mine',
  'archery_range',
  'stable',
  'hospital',
  'embassy',
  'market',
  'academy',
  'headquarters',
] as const;
export type BuildingType = (typeof BUILDING_TYPES)[number];

/** Где стоит здание: center — ратуша, castle — 40 участков в замке, field — 200 участков поля, wall — стена. */
export const BUILDING_ZONES = ['center', 'castle', 'field', 'wall'] as const;
export type BuildingZone = (typeof BUILDING_ZONES)[number];
/** Добывающие здания поля ресурсов. */
export const FIELD_BUILDINGS = ['farm', 'sawmill', 'quarry', 'iron_mine', 'silver_mine'] as const;

/** peasant — крестьяне: нанимаются в ратуше, добывают в шахтах; в атаку и разведку не ходят. */
export const UNIT_CLASSES = ['infantry', 'archer', 'cavalry', 'siege', 'scout', 'peasant'] as const;
export type UnitClass = (typeof UNIT_CLASSES)[number];

export const SHIP_TYPES = [
  'sloop',
  'brig',
  'frigate',
  'galleon',
  'ship_of_line',
  'transport',
  'cog',
] as const;
export type ShipType = (typeof SHIP_TYPES)[number];

// Новые типы — только в конец: индекс используется в сетке местности
export const TERRAINS = [
  'plain',
  'forest',
  'mountain',
  'river',
  'sea',
  'coast',
  'desert',
  'snow',
  'hill',
  'bridge',
  'road',
] as const;
export type Terrain = (typeof TERRAINS)[number];

export const TILE_OBJECTS = [
  'castle',
  'abandoned',
  'port',
  'pirate_haven',
  'capital',
  'keypoint',
  'oasis',
  'barbarian_camp',
  'boss',
  'neutral_city',
  'deposit',
] as const;
export type TileObjectType = (typeof TILE_OBJECTS)[number];

/** transfer — перевести войска в гарнизон своего замка (принимают расу замка); reinforce — встать отдельным отрядом. */
export const MARCH_TYPES = [
  'attack',
  'capture',
  'scout',
  'reinforce',
  'gather',
  'transport',
  'transfer',
] as const;
export type MarchType = (typeof MARCH_TYPES)[number];

/** Диверсия разведчиков после успешной разведки замка: отравить воду, отравить еду, взять пленных, заложить бомбу. */
export const SABOTAGE_TYPES = ['poison_water', 'poison_food', 'capture', 'bomb'] as const;
export type SabotageType = (typeof SABOTAGE_TYPES)[number];

/** Фитили бомбы: нападающий выбирает настоящий, два других — пустышки; защитник режет один из них. */
export const BOMB_WIRES = ['red', 'green', 'blue'] as const;
export type BombWire = (typeof BOMB_WIRES)[number];

export const SEA_MARCH_TYPES = [
  'trade',
  'war',
  'pirate',
  'scout',
  'blockade',
  'treasure',
  'fishing',
  'landing',
  'monster',
  'patrol',
  'conquest',
  'evacuate',
  /** Перегон кораблей в другую свою гавань (как перевод войск между замками). */
  'transfer',
  /** Доставка ресурсов из замка гавани отправления в замок своей или союзной гавани. */
  'delivery',
] as const;
export type SeaMarchType = (typeof SEA_MARCH_TYPES)[number];

export const ALLIANCE_ROLES = ['leader', 'officer', 'diplomat', 'treasurer', 'member'] as const;
export type AllianceRole = (typeof ALLIANCE_ROLES)[number];

export const DIPLOMACY_TYPES = ['war', 'peace', 'neutral', 'pact', 'coalition'] as const;
export type DiplomacyType = (typeof DIPLOMACY_TYPES)[number];

export const CHAT_CHANNELS = ['global', 'alliance', 'private', 'coalition', 'system'] as const;
export type ChatChannel = (typeof CHAT_CHANNELS)[number];

export const ADMIN_ROLES = ['superadmin', 'admin', 'moderator', 'support'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const BOOSTER_TYPES = [
  'build_speed',
  'train_speed',
  'resource_boost',
  'shield',
  'tax_exempt',
] as const;
export type BoosterType = (typeof BOOSTER_TYPES)[number];

export function emptyResources(): ResourceMap {
  return { silver: 0, wood: 0, stone: 0, food: 0, iron: 0 };
}

export interface Point {
  x: number;
  y: number;
}

/** Стандартный формат ошибки API. */
export interface ApiError {
  error: { code: string; message: string; details?: unknown };
}
